import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ReferenceLine,
  ReferenceArea,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
} from "recharts";
import type { TooltipProps } from "recharts";
import { formatRankLabel } from "../utils/rankingResultsUtils";
import { ScatterNode, SCATTER_POINT_SIZE } from "./shared/ScatterPlotShared";

export interface ScatterPoint {
  optionId?: string;
  label: string;
  imageUrl?: string;
  isRankedByUser: boolean;
  averageRank: number;
  bestMinimumRank: number;
  bestMinimumRankLabel: string;
  minimumSupportCount: number;
  minimumSupportShare: number;
  averageRankPosition: number;
  minimumSupportRankPosition: number;
  averageRankLabel: string;
  minimumSupportRankLabel: string;
  userRank?: number;
  userRankLabel: string;
  xScore: number;
  yScore: number;
  labelOffsetY?: number;
  labelSide?: "left" | "right";
}

export interface RankingScatterPlotProps {
  data: ScatterPoint[];
  xTicks: number[];
  xLabel: string;
  yLabel: string;
  scoreToRank: (score: number) => number;
  maxRankScore: number;
}

const ScatterTooltip = ({ active, payload }: TooltipProps<number, string>) => {
  if (!active || !payload?.length || !payload[0]?.payload) return null;
  const point = payload[0].payload as ScatterPoint;
  return (
    <div className="rounded-lg border bg-background p-3 shadow-md text-sm space-y-1 max-w-xs">
      <p className="font-semibold leading-tight">{point.label}</p>
      <p className="text-muted-foreground">Your rank: {point.userRankLabel}</p>
      <p className="text-muted-foreground">
        Rank by average: {point.averageRankLabel} (avg {point.averageRank ? point.averageRank.toFixed(1) : "—"})
      </p>
      <p className="text-muted-foreground">
        Rank by minimum support: {point.minimumSupportRankLabel}
      </p>
      <p className="text-muted-foreground">Strongest required rank: {point.bestMinimumRankLabel}</p>
      <p className="text-muted-foreground">
        Within required ranks: {point.minimumSupportCount} response{point.minimumSupportCount === 1 ? "" : "s"} (
        {point.minimumSupportShare.toFixed(1)}%)
      </p>
    </div>
  );
};

export const RankingScatterPlot = ({
  data,
  xTicks,
  xLabel,
  yLabel,
  scoreToRank,
  maxRankScore,
}: RankingScatterPlotProps) => {
  const maxRankValue = scoreToRank(1);
  const xTickFormatter = (score: number) => {
    const rankValue = scoreToRank(score);
    if (rankValue > maxRankValue) return "Unranked";
    return formatRankLabel(Math.round(rankValue));
  };

  const yTickFormatter = (score: number) => {
    const rankValue = scoreToRank(score);
    if (rankValue > maxRankValue) return "Unranked";
    return formatRankLabel(Math.round(rankValue));
  };
  const unrankedBandPadding = 0.8;
  const unrankedBandTop = 0.9;
  const yDomain: [number, number] = [-unrankedBandPadding, maxRankScore];
  return (
    <div className="w-full h-[420px]">
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 52, right: 48, bottom: 64, left: 72 }}>
          <XAxis
            type="number"
            dataKey="xScore"
            domain={[0, maxRankScore]}
            ticks={xTicks}
            tickFormatter={xTickFormatter}
            label={{ value: xLabel, position: "insideBottom", offset: -45, fill: "#475569" }}
            padding={{ left: 10, right: 10 }}
          />
          <YAxis
            type="number"
            dataKey="yScore"
            domain={yDomain}
            ticks={xTicks}
            tickFormatter={yTickFormatter}
            label={{
              value: yLabel,
              position: "insideLeft",
              angle: -90,
              offset: -10,
              fill: "#475569",
              style: { textAnchor: "middle" },
            }}
            padding={{ top: 10, bottom: 16 }}
          />
          <ReferenceArea
            y1={unrankedBandTop}
            y2={maxRankScore}
            fill="rgba(59, 130, 246, 0.08)"
            label={{
              value: "Ranked higher than average",
              position: "insideTopLeft",
              fill: "#1d4ed8",
              fontSize: 12,
              fontWeight: 600,
            }}
          />
          <ReferenceArea
            y1={-unrankedBandPadding}
            y2={unrankedBandTop}
            fill="rgba(248, 113, 113, 0.08)"
          />
          <ReferenceLine
            segment={[
              { x: 1, y: 1 },
              { x: maxRankScore, y: maxRankScore },
            ]}
            stroke="#94a3b8"
            strokeWidth={2}
            strokeDasharray="6 6"
          />
          <RechartsTooltip content={<ScatterTooltip />} cursor={{ strokeDasharray: "3 3" }} />
          <Scatter data={data} shape={(props) => <ScatterNode {...props} size={SCATTER_POINT_SIZE} />} />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
};
